import { graphql } from 'gql.tada';
import { useQuery } from '@apollo/client';
import { useEffect } from 'react';
import type { ResultOf } from 'gql.tada';

const FamilyBillingQuery = graphql.persisted(
  "FamilyBilling",
  graphql(`
    query FamilyBilling($input: FamilyBillingInput!) {
      familyBilling(input: $input) {
        familyId
        familyName
        familySlug
        schoolYearId
        schoolYearName
        familyEnrollmentId
        enrollmentState
        stripeAccountId
        paymentMethod {
          summary
          methodType
          last4
          autopayEnabled
          usableForAutomaticBilling
          stripeCustomerId
          stripeDashboardUrl
        }
        familyAutoPayAuthorized
        autoPayWaived
        autoPayWaivedReason
        subscriptions {
          subscriptionId
          stripeSubscriptionId
          status
          startDate
          billingDayOfMonth
          cancelAtPeriodEnd
          cancelAt
          currentPeriodEndAt
          hasPaymentProcessingInvoice
          converted
          locallyManaged
          stripeDashboardUrl
          invoiceCount
          totalCents
          paymentMethodSummary
          usesDefaultPaymentMethod
          lastDriftCheckAt
          driftDetected
          driftDetails {
            localDate
            stripeDate
            differenceDays
            message
          }
          invoices {
            invoiceId
            subscriptionId
            key
            scheduleKey
            status
            displayStatus
            amountCents
            dueDate
            postedAt
            noticeStatus
            lastNoticeAt
            canResendNotice
            noticeOutcomeUnknown
            noticeRecipients { name email status reason outcomeUnknown }
            memo
            stripeCheckoutSessionId
            stripePaymentIntentId
            stripeInvoiceId
            stripeHostedInvoiceUrl
            stripeInvoicePdfUrl
            stripeDashboardUrl
            checkoutPaymentStatus
            autopayStatus
            autopayFailureReason
            subscriptionManaged
            stripeCollectionMethod
            automaticPaymentAuthorized
            postable
            postDisabledReason
            editable
            warningCodes
            lineItems {
              lineItemId
              description
              amountCents
              lineType
            }
          }
        }
        invoices {
          invoiceId
          subscriptionId
          key
          scheduleKey
          status
          displayStatus
          amountCents
          dueDate
          postedAt
          noticeStatus
          lastNoticeAt
          canResendNotice
          noticeOutcomeUnknown
          noticeRecipients { name email status reason outcomeUnknown }
          memo
          stripeCheckoutSessionId
          stripePaymentIntentId
          stripeInvoiceId
          stripeHostedInvoiceUrl
          stripeInvoicePdfUrl
          stripeDashboardUrl
          checkoutPaymentStatus
          autopayStatus
          autopayFailureReason
          subscriptionManaged
          stripeCollectionMethod
          automaticPaymentAuthorized
          postable
          postDisabledReason
          editable
          warningCodes
          lineItems {
            lineItemId
            description
            amountCents
            lineType
          }
        }
        warnings {
          code
          message
          severity
        }
      }
    }
  `)
);

type FamilyBillingResult = NonNullable<ResultOf<typeof FamilyBillingQuery>>;
export type FamilyBillingData = NonNullable<FamilyBillingResult['familyBilling']>;
export type FamilyBillingInvoice = FamilyBillingData['invoices'][number];
export type FamilyBillingSubscription = FamilyBillingData['subscriptions'][number];
export type FamilyBillingWarning = FamilyBillingData['warnings'][number];
export type FamilyBillingInvoiceLine = FamilyBillingInvoice['lineItems'][number];
export type FamilyBillingDriftDetails = NonNullable<FamilyBillingSubscription['driftDetails']>;

export { FamilyBillingQuery };

export function useFamilyBillingQuery(
  schoolId: string | undefined,
  familySlug: string | undefined,
  schoolYearId: string | undefined,
  enabled = true
) {
  const result = useQuery(FamilyBillingQuery, {
    variables: {
      input: {
        schoolId: schoolId!,
        familySlug: familySlug!,
        schoolYearId: schoolYearId!,
      },
    },
    skip: !enabled || !schoolId || !familySlug || !schoolYearId,
  });
  const billing = result.data?.familyBilling;
  const awaitingDelivery = [...(billing?.invoices ?? []), ...(billing?.subscriptions.flatMap((subscription) => subscription.invoices) ?? [])]
    .some((invoice) => invoice.noticeStatus === 'Queued');
  const { startPolling, stopPolling } = result;
  useEffect(() => {
    if (enabled && awaitingDelivery) startPolling(10_000);
    else stopPolling();
    return stopPolling;
  }, [enabled, awaitingDelivery, startPolling, stopPolling]);
  return result;
}
